import React, { useMemo, useState } from "react";
import { ArrowLeft, Lock, Sparkles, Database, FileText } from "lucide-react";
import "./source-evidence.css";

const money = (v) =>
  v == null || v === "" || isNaN(Number(v)) ? null
    : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LEVELS = [["primary", "PDX"], ["dx2", "DX2"], ["dx3", "DX3"], ["dx4", "DX4"]];
const Dev = () => null;

export default function SourceEvidence({ note, onBack }) {
  const [doc, setDoc] = useState("parsed"); // parsed | note
  const [tab, setTab] = useState("evidence"); // evidence | extracted | edi

  const icds = useMemo(() => (Array.isArray(note?.icd_codes) ? note.icd_codes : []), [note]);
  const cpts = useMemo(() => (Array.isArray(note?.cpt_codes) ? note.cpt_codes : []), [note]);
  const vr = note?.validation_report || {};
  const scope = vr.scope || null;
  const combo = vr.billing_combo || null;
  const boLines = vr.billing_output?.lines || [];

  const posText = note?.place_of_service?.description
    ? `(${note?.pos_code || "—"}) ${note.place_of_service.description}`
    : (note?.pos_code ? `POS ${note.pos_code}` : "—");

  // Aggregate the real per-(cpt,dx) corpus signals for each diagnosis code.
  const dxAgg = useMemo(() => {
    const m = {};
    cpts.forEach((c) => (Array.isArray(c.dx) ? c.dx : []).forEach((dx) => {
      if (!dx?.code) return;
      const a = m[dx.code] || (m[dx.code] = { corpus: new Set(), assigned: new Set(), score: 0 });
      (dx.corpus_levels || []).forEach((l) => a.corpus.add(l));
      if (dx.level) a.assigned.add(dx.level);
      a.score = Math.max(a.score, dx.score || 0);
    }));
    return m;
  }, [cpts]);

  const knownCount = icds.filter((i) => i.seen_before || i.validation?.in_corpus).length;

  // Suggestions surfaced per CPT (real cpt.suggestions strings).
  const suggestions = useMemo(() => {
    const out = [];
    cpts.forEach((c) => (Array.isArray(c.suggestions) ? c.suggestions : []).forEach((s) => {
      const text = typeof s === "string" ? s : (s?.text || s?.message || "");
      if (text) out.push({ cpt: c.code, text });
    }));
    return out;
  }, [cpts]);

  const scopeLabel = scope?.used === "taxonomy" ? "Taxonomy-only" : (scope?.used ? scope.used : "Corpus");

  return (
    <div className="se-overlay" data-testid="source-evidence" role="dialog" aria-label="View source and evidence">
      {/* header */}
      <div className="se-head">
        <button className="se-back" onClick={onBack} data-testid="se-back"><ArrowLeft size={15} /> Back to claim</button>
        <div className="se-head-txt">
          <div className="se-head-t">View source / evidence — {note?.patient_name || "—"}</div>
          <div className="se-head-s">Source document vs. what revQ captured</div>
        </div>
        <div className="se-lock"><Lock size={12} /> Encrypted · read-only</div>
      </div>

      <div className="se-body">
        {/* LEFT — source document */}
        <div className="se-left">
          <div className="se-left-bar">
            <span className="se-src-lbl">Source document · {posText}</span>
            <div className="se-doc-toggle" data-testid="se-doc-toggle">
              <button className={doc === "parsed" ? "on" : ""} onClick={() => setDoc("parsed")} data-testid="se-doc-parsed">revQ-parsed</button>
              <button className={doc === "note" ? "on" : ""} onClick={() => setDoc("note")} data-testid="se-doc-note">Encounter note</button>
            </div>
          </div>
          <div className="se-doc" data-testid="se-doc-content">
            {doc === "note" ? (
              note?.note_text
                ? <pre className="se-raw">{note.note_text}</pre>
                : <div className="se-noraw">No raw source text is stored for this document type ({note?.source_type || "—"}). The revQ-parsed view reflects everything the engine captured.</div>
            ) : (
              <ParsedDoc note={note} icds={icds} cpts={cpts} />
            )}
          </div>
        </div>

        {/* RIGHT — evidence */}
        <div className="se-right">
          <div className="se-tabs" data-testid="se-tabs">
            <button className={tab === "evidence" ? "on" : ""} onClick={() => setTab("evidence")} data-testid="se-tab-evidence">revQ evidence</button>
            <button className={tab === "extracted" ? "on" : ""} onClick={() => setTab("extracted")} data-testid="se-tab-extracted">Extracted</button>
            <button className={tab === "edi" ? "on" : ""} onClick={() => setTab("edi")} data-testid="se-tab-edi">Claim lines</button>
          </div>

          {tab === "evidence" ? (
            <div className="se-tabbody" data-testid="se-evidence">
              <p className="se-lede">The evidence trail revQ built for this claim — suggestions, validation source, and per-code audit. Each card is drawn from the coded note.</p>

              {suggestions.length ? (
                <div className="se-sug" data-testid="se-suggestions">
                  <div className="se-sug-lbl">Suggestions</div>
                  {suggestions.map((s, i) => (
                    <div key={i} className="se-sug-row"><Sparkles size={12} /> <b>{s.cpt}</b> — {s.text}</div>
                  ))}
                </div>
              ) : null}

              {scope ? (
                <div className="se-valsrc" data-testid="se-validation-source">
                  <div className="se-valsrc-t"><Database size={13} /> Validation source: <b>{scopeLabel}</b>{typeof scope.records === "number" ? ` · ${scope.records.toLocaleString()} records` : ""}</div>
                  <div className="se-valsrc-s">{scope.reason || `Validated against the ${scopeLabel.toLowerCase()} reference${scope.taxonomy_code ? ` for taxonomy ${scope.taxonomy_code}` : ""}.`}</div>
                </div>
              ) : null}

              <div className="se-dxhead">
                <span className="se-dxhead-t"><Sparkles size={12} /> Diagnosis · ICD-10 <span className="se-dxhead-n">({icds.length})</span></span>
                <span className="se-dxhead-badge">{knownCount}/{icds.length} known pairings</span>
              </div>
              <p className="se-dxsub">Documented diagnoses captured from the note. <b>Valid as</b> shows the corpus pointer levels this code supports — the actual per-line pointer is assigned by the procedure agent.</p>

              {icds.length ? icds.map((icd) => {
                const agg = dxAgg[icd.code] || { corpus: new Set(), assigned: new Set(), score: 0 };
                const documented = icd.provenance === "clear";
                const known = icd.seen_before || icd.validation?.in_corpus;
                return (
                  <div key={icd.code} className={`se-dxcard ${agg.assigned.has("primary") ? "primary" : ""}`} data-testid={`se-icd-${icd.code}`}>
                    <div className="se-dxcard-h"><span className="se-dxcode">{icd.code}</span><span className="se-dxname">{icd.description}</span></div>
                    <div className="se-dxtags">
                      {documented ? <span className="se-tag doc">documented</span> : null}
                      <span className={`se-tag ${known ? "known" : "new"}`}>{known ? "known pairing" : "new pairing"}</span>
                    </div>
                    {icd.evidence ? <div className="se-quote">“{icd.evidence}”</div> : null}
                    {icd.rationale && icd.rationale !== icd.evidence ? <div className="se-rationale">↳ {icd.rationale}</div> : null}
                    <div className="se-validas">
                      <span className="se-validas-l">Valid as</span>
                      {LEVELS.map(([k, lbl]) => {
                        const on = agg.corpus.has(k);
                        const assigned = agg.assigned.has(k);
                        return <span key={k} className={`se-lv ${on ? "on" : ""} ${assigned ? "assigned" : ""}`}>{lbl}{assigned ? " ✓" : ""}</span>;
                      })}
                      {agg.score ? <span className="se-score">corpus support {agg.score.toLocaleString()}</span> : null}
                    </div>
                    {combo?.cpts?.length ? (
                      <div className="se-pairs">Pairs with (corpus indicators): <b>{combo.cpts.join(", ")}</b> · informational — the billed CPTs come from the procedure agent.</div>
                    ) : null}
                  </div>
                );
              }) : <div className="se-empty">No diagnoses were coded from this document.</div>}
              <Dev>per-level probability bars aren’t in the API — showing real corpus level-eligibility &amp; support score</Dev>
            </div>
          ) : tab === "extracted" ? (
            <div className="se-tabbody" data-testid="se-extracted">
              <ExtractedView note={note} icds={icds} cpts={cpts} />
            </div>
          ) : (
            <div className="se-tabbody" data-testid="se-edi">
              <p className="se-lede">Assembled claim lines (Box 24) with their diagnosis pointers.</p>
              {boLines.length ? boLines.map((l, i) => (
                <div key={i} className="se-edi-line">
                  <div className="se-edi-code"><b>{l.code}</b> {l.description}</div>
                  <div className="se-edi-meta">
                    DX pointers: {(l.dx || []).join(", ") || "—"}
                    {(l.modifiers || []).length ? ` · Modifiers: ${l.modifiers.join(", ")}` : ""}
                  </div>
                </div>
              )) : <div className="se-empty">No assembled claim lines available.</div>}
              <Dev>the full X12 837 string isn’t returned by the API — showing the assembled claim lines</Dev>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ParsedDoc({ note, icds, cpts }) {
  return (
    <div className="se-parsed" data-testid="se-parsed-doc">
      <div className="se-parsed-h">ENCOUNTER — WHAT REVQ CAPTURED</div>
      <div className="se-kvline"><b>{note?.patient_name || "—"}</b>{note?.date_of_service ? ` · DOS ${note.date_of_service}` : ""}</div>
      <div className="se-kvline">Provider: {note?.provider_name || "—"}{note?.provider_state ? ` (${note.provider_state})` : ""}</div>
      <div className="se-kvline se-muted">
        {note?.mrn ? `MRN ${note.mrn}` : "no MRN"}
        {note?.patient_dob ? ` · DOB ${note.patient_dob}` : ""}
        {note?.patient_gender ? ` · ${note.patient_gender}` : ""}
      </div>
      <div className="se-kvline se-muted">Payer: {note?.payer_name || "Not found"}{note?.member_id ? ` · ${note.member_id}` : ""}</div>

      <div className="se-parsed-sec">DIAGNOSES (ICD-10)</div>
      {icds.length ? icds.map((d, i) => (
        <div key={d.code} className="se-parsed-item"><span className="se-pn">{i + 1}.</span> {d.description} <span className="se-pc">({d.code})</span></div>
      )) : <div className="se-parsed-item se-muted">None coded.</div>}

      <div className="se-parsed-sec">PLAN / ORDERS</div>
      {cpts.length ? cpts.map((c, i) => (
        <div key={`${c.code}-${i}`} className="se-parsed-item">
          <span className="se-pn">·</span> {c.description} <span className="se-pc">(CPT {c.code}{(c.modifiers || []).length ? `-${c.modifiers.join(",")}` : ""})</span>
          {money(c.avg_paid?.amt) ? <span className="se-pamt">{money(c.avg_paid.amt)}</span> : null}
        </div>
      )) : <div className="se-parsed-item se-muted">None coded.</div>}
    </div>
  );
}

function KV({ k, v }) {
  return <div className="se-ex-kv"><span className="k">{k}</span><span className="v">{v || "—"}</span></div>;
}

function ExtractedView({ note, icds, cpts }) {
  return (
    <div className="se-ex">
      <div className="se-ex-sec">Patient</div>
      <KV k="Name" v={note?.patient_name} />
      <KV k="MRN" v={note?.mrn} />
      <KV k="DOB" v={note?.patient_dob} />
      <KV k="Gender" v={note?.patient_gender} />
      <KV k="Age" v={note?.patient_age} />

      <div className="se-ex-sec">Insurance</div>
      <KV k="Payer" v={note?.payer_name} />
      <KV k="Member ID" v={note?.member_id} />
      <KV k="Payer ID" v={note?.payer_id} />

      <div className="se-ex-sec">Provider &amp; encounter</div>
      <KV k="Provider" v={note?.provider_name} />
      <KV k="NPI" v={note?.npi_number} />
      <KV k="Taxonomy" v={note?.taxonomy_desc ? `${note.taxonomy_desc}${note?.taxonomy_code ? ` (${note.taxonomy_code})` : ""}` : note?.taxonomy_code} />
      <KV k="Date of service" v={note?.date_of_service} />
      <KV k="Place of service" v={note?.place_of_service?.description ? `${note.place_of_service.description}${note?.pos_code ? ` · ${note.pos_code}` : ""}` : note?.pos_code} />
      <KV k="Source" v={`${note?.source_type || "—"}${note?.filename ? ` · ${note.filename}` : ""}`} />

      <div className="se-ex-sec">Diagnoses ({icds.length})</div>
      {icds.length ? icds.map((d) => <div key={d.code} className="se-ex-code"><b>{d.code}</b> {d.description}</div>) : <div className="se-ex-kv se-muted">None</div>}

      <div className="se-ex-sec">Procedures ({cpts.length})</div>
      {cpts.length ? cpts.map((c, i) => (
        <div key={`${c.code}-${i}`} className="se-ex-code"><b>{c.code}</b> {c.description}{c.avg_paid?.amt ? ` · $${Number(c.avg_paid.amt).toFixed(2)} est` : ""}</div>
      )) : <div className="se-ex-kv se-muted">None</div>}
    </div>
  );
}
